import React from "react";
import { Link } from "@reach/router";
import moment from "moment";
import { useQuery } from "@apollo/react-hooks";
import BackLink from "../../../components/BackLink";
import { GET_FRANCHISE } from "../services/graphql";
import styles from "../Franchises.module.scss";

const Franchise = ({ franchiseId }) => {
  const { data, loading, error } = useQuery(GET_FRANCHISE, {
    variables: { franchiseId }
  });
  const franchise = data && data.franchise;
  const schoolCounts = (franchise && franchise.schoolsCountByStatus) || {};
  const memberships = (franchise && franchise.memberships) || [];
  const recentSchools = (franchise && franchise.recentSchools) || [];

  return (
    <div className={styles.franchises}>
      <BackLink text="Franchises" to="/franchises" />
      {loading && <p role="status">Loading franchise...</p>}
      {error && <p role="alert">Unable to load franchise. Please try again later.</p>}
      {!loading && !error && !franchise && <p>Franchise not found.</p>}
      {!loading && !error && franchise && <>
        <div className={`${styles.header} ${styles.detailHeader}`}>
          <div className={styles.territoryDetails}>
            <span className={styles.countryCode}>{franchise.countryCode}</span>
            <div>
              <h1 className="title">{franchise.name} franchise</h1>
              <p>Boom staff oversight{franchise.leadUserName && ` · Managed locally by ${franchise.leadUserName}`}</p>
            </div>
          </div>
          <a href={`/franchise-portal/${franchiseId}`} target="_blank" rel="noopener noreferrer" className={styles.primaryButton}>Open {franchise.name} portal</a>
        </div>

        <div className={styles.section}>
          <h2>Territory overview</h2>
          <div className={styles.overview}>
            <div className={styles.overviewCard}>
              <h3>Schools</h3>
              <strong>{(franchise.totalSchoolsCount || 0).toLocaleString("en-GB")}</strong>
            </div>
            <div className={styles.overviewCard}>
              <h3>Pupil licences</h3>
              <strong>{(franchise.totalPupilLicences || 0).toLocaleString("en-GB")}</strong>
            </div>
            <div className={styles.overviewCard}>
              <h3>Teachers</h3>
              <strong>{(franchise.totalTeachers || 0).toLocaleString("en-GB")}</strong>
            </div>
            <div className={styles.overviewCard}>
              <h3>Parents</h3>
              <strong>{(franchise.totalParents || 0).toLocaleString("en-GB")}</strong>
            </div>
          </div>
        </div>

        <div className={styles.detailPanels}>
          <div className={styles.detailPanel}>
            <h2>School onboarding</h2>
            <div className={styles.onboardingContent}>
              <ul className={styles.onboardingList}>
                <li>
                  <span className={styles.activeDot} aria-hidden="true" />
                  <strong>{schoolCounts.active || 0}</strong>
                  <span>Active schools</span>
                </li>
                <li>
                  <span className={styles.pendingDot} aria-hidden="true" />
                  <strong>{schoolCounts.needsAdmin || 0}</strong>
                  <span>Need an administrator</span>
                </li>
                <li>
                  <span className={styles.pendingDot} aria-hidden="true" />
                  <strong>{schoolCounts.draft || 0}</strong>
                  <span>Draft school records</span>
                </li>
              </ul>
              <Link to={`/franchises/${franchiseId}/schools`} className={styles.button}>View schools</Link>
            </div>
          </div>

          <div className={styles.detailPanel}>
            <h2>{franchise.name} franchise admins</h2>
            {memberships.length === 0 && <p>No franchise admins yet.</p>}
            <ul className={memberships.length > 3 ? `${styles.adminList} ${styles.adminScroll}` : styles.adminList}>
              {memberships.map(membership => (
                <li key={membership.id}>
                  <strong>{membership.userName}</strong>
                  <span className={styles.detailStatus} data-status={(membership.statusLabel || "").toLowerCase()}>
                    {membership.statusLabel}
                  </span>
                </li>
              ))}
            </ul>
            <div className={styles.panelAction}>
              <Link to={`/franchises/${franchiseId}/admins`} className={styles.textButton}>Manage admins</Link>
            </div>
          </div>
        </div>

        <div className={`${styles.addTerritory} ${styles.financeSummary}`}>
          <span className={`${styles.addIcon} ${styles.currencyBadge}`}>{franchise.currency}</span>
          <div className={styles.addDetails}>
            <h3>Revenue and invoicing</h3>
            <p>10% current band · 24 lifetime schools · 27 schools to enter the 15% band</p>
          </div>
          <Link to={`/franchises/${franchiseId}/finance`} className={styles.primaryButton}>View finance</Link>
        </div>

        <div className={styles.section}>
          <h2>Recently added schools</h2>
          {recentSchools.length === 0 ? <p>No recently added schools.</p> : (
            <div className={recentSchools.length > 3 ? `${styles.schoolsTableWrapper} ${styles.schoolsScroll}` : styles.schoolsTableWrapper}>
              <table className={styles.schoolsTable}>
                <thead>
                  <tr>
                    <th scope="col">School</th>
                    <th scope="col">Pupils</th>
                    <th scope="col">Start date</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSchools.map(school => (
                    <tr key={school.id}>
                      <th scope="row"><Link to={`/franchises/${franchiseId}/schools/${school.id}`}>{school.name}</Link></th>
                      <td>{(school.pupilsCount || 0).toLocaleString("en-GB")}</td>
                      <td>{school.startDate ? moment(school.startDate).format("D MMM YYYY") : "—"}</td>
                      <td>
                        <span className={styles.detailStatus} data-status={(school.onboardingStatusLabel || "").toLowerCase()}>
                          {school.onboardingStatusLabel}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>}
    </div>
  );
};

export default Franchise;
